import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Trash2, ArrowLeft, Shield } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import ConfirmDialog from '../components/ConfirmDialog';
import fetchWithTimeout from '../lib/fetchWithTimeout';
import { API_URL as BACKEND_URL } from '../lib/apiUrl';

export default function DeleteAccount() {
  const { user, signOut, getAccessToken } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const [showConfirm, setShowConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async () => {
    setDeleting(true);
    try {
      const token = await getAccessToken();
      const response = await fetchWithTimeout(`${BACKEND_URL}/api/user/delete-account`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to delete account');

      await signOut();
      navigate('/');
      toast.success('Your account has been permanently deleted.');
    } catch (error) {
      toast.error(error.message || 'Error deleting account. Please contact privacy@wiredcop.com.');
    } finally {
      setDeleting(false);
      setShowConfirm(false);
    }
  };

  if (!user) {
    return (
      <div className="app-container">
        <div className="app-wrapper">
          <div className="app-card legal-page">
            <div className="app-header">
              <h1>
                <Trash2 className="header-icon" />
                Delete Account
              </h1>
              <p>You must be signed in to delete your account</p>
            </div>
            <div className="legal-content">
              <section>
                <p>Please sign in first, then return to this page to delete your account and all associated data.</p>
                <p>
                  Alternatively, you can email <strong>privacy@wiredcop.com</strong> from the
                  email address associated with your account to request deletion.
                </p>
              </section>
            </div>
            <div className="legal-footer">
              <Link to="/login" className="btn btn-yellow">Sign In</Link>
              <Link to="/" className="btn btn-dark">
                <ArrowLeft size={18} /> Back to App
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="app-container">
      <div className="app-wrapper">
        <div className="app-card legal-page">
          <div className="app-header">
            <h1>
              <Trash2 className="header-icon" />
              Delete Account
            </h1>
            <p>Permanently remove your account and data</p>
          </div>

          <div className="legal-content">
            <section>
              <h2>What will be deleted</h2>
              <ul>
                <li>Your account and login credentials</li>
                <li>All search history</li>
                <li>All shared trip links</li>
                <li>Usage tracking data</li>
                <li>Your profile information</li>
              </ul>
            </section>

            <section>
              <h2>Subscription</h2>
              <p>
                If you have an active paid subscription, it will be cancelled immediately.
                No further charges will be made.
              </p>
            </section>

            <section>
              <h2>This action is permanent</h2>
              <p>
                Once deleted, your data cannot be recovered. If you change your mind,
                you will need to create a new account.
              </p>
            </section>

            <div style={{ marginTop: '2rem' }}>
              <p><strong>Signed in as:</strong> {user.email}</p>
              <button
                onClick={() => setShowConfirm(true)}
                disabled={deleting}
                className="btn btn-red"
                style={{ marginTop: '1rem' }}
              >
                <Trash2 size={16} />
                {deleting ? 'Deleting...' : 'Delete My Account'}
              </button>
            </div>
          </div>

          <ConfirmDialog
            isOpen={showConfirm}
            title="Delete Account Permanently?"
            message={`This will permanently delete your account (${user.email}) and all associated data. This cannot be undone.`}
            confirmText={deleting ? 'Deleting...' : 'Yes, Delete Everything'}
            cancelText="Cancel"
            onConfirm={handleDelete}
            onCancel={() => setShowConfirm(false)}
            variant="warning"
          />

          <div className="legal-footer">
            <Link to="/account" className="btn btn-dark">
              <ArrowLeft size={18} /> Back to Account
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
